import { makeAutoObservable, runInAction } from "mobx";
import { superadminGetServiceCenterTransactions } from "@pro-sto/api-client";
import type {
  TransactionItemDto as TransactionItem,
  PaginationMetaDto as PaginationMeta,
  SuperadminGetServiceCenterTransactionsData,
} from "@pro-sto/api-client";
import { toastStore } from "../ToastStore";

const DEFAULT_META: PaginationMeta = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPrevPage: false,
};

export class TransactionsSubStore {
  items: TransactionItem[] = [];
  meta: PaginationMeta = { ...DEFAULT_META };
  isLoading = false;

  /** Monotonic counter to discard stale responses */
  private reqSeq = 0;

  search = "";
  typeFilter: string[] = [];
  statusFilter: string[] = [];
  sort = "-date";
  dateFrom = "";
  dateTo = "";

  constructor(private getUuid: () => string) {
    makeAutoObservable<this, 'reqSeq'>(this, { reqSeq: false });
  }

  setSearch = (q: string) => {
    this.search = q;
  };
  setTypeFilter = (val: string[]) => {
    this.typeFilter = val;
  };
  setStatusFilter = (val: string[]) => {
    this.statusFilter = val;
  };
  setSort = (sort: string) => {
    this.sort = sort;
  };
  setDateRange = (from: string, to: string) => {
    this.dateFrom = from;
    this.dateTo = to;
  };

  private buildParams(
    page: number,
  ): Omit<SuperadminGetServiceCenterTransactionsData, "uuid"> {
    return {
      page,
      limit: this.meta.limit,
      ...(this.search && { q: this.search }),
      ...(this.sort && { sort: this.sort }),
      ...(this.typeFilter.length > 0 && {
        filterTypeEq: this
          .typeFilter[0] as SuperadminGetServiceCenterTransactionsData["filterTypeEq"],
      }),
      ...(this.statusFilter.length > 0 && {
        filterStatusEq: this
          .statusFilter[0] as SuperadminGetServiceCenterTransactionsData["filterStatusEq"],
      }),
      ...(this.dateFrom && { filterDateFromEq: this.dateFrom }),
      ...(this.dateTo && { filterDateToEq: this.dateTo }),
    };
  }

  load = async (page = 1) => {
    const uuid = this.getUuid();
    if (!uuid) return;
    const reqId = ++this.reqSeq;
    this.isLoading = true;
    try {
      const res = await superadminGetServiceCenterTransactions({
        uuid,
        ...this.buildParams(page),
      });
      if (reqId !== this.reqSeq) return;
      runInAction(() => {
        this.items = res.data ?? [];
        this.meta = res.meta;
        this.isLoading = false;
      });
    } catch {
      if (reqId !== this.reqSeq) return;
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError("Не удалось загрузить историю операций");
    }
  };

  loadMore = async () => {
    if (!this.meta.hasNextPage || this.isLoading) return;
    const uuid = this.getUuid();
    if (!uuid) return;
    const reqId = ++this.reqSeq;
    this.isLoading = true;
    try {
      const res = await superadminGetServiceCenterTransactions({
        uuid,
        ...this.buildParams(this.meta.page + 1),
      });
      if (reqId !== this.reqSeq) return;
      runInAction(() => {
        const existingUuids = new Set(this.items.map((i) => i.uuid));
        const newItems = (res.data ?? []).filter(
          (i) => !existingUuids.has(i.uuid),
        );
        this.items = [...this.items, ...newItems];
        this.meta = res.meta;
        this.isLoading = false;
      });
    } catch {
      if (reqId !== this.reqSeq) return;
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError("Не удалось загрузить историю операций");
    }
  };

  reset = () => {
    this.items = [];
    this.meta = { ...DEFAULT_META };
    this.search = "";
    this.typeFilter = [];
    this.statusFilter = [];
    this.sort = "-date";
    this.dateFrom = "";
    this.dateTo = "";
  };
}
